<script setup>
import { ref, watch, defineProps, computed, nextTick } from 'vue'
import FlipDigitPart from './FlipDigitPart.vue'
const gsapTimeline = useGsapTimeline()

const props = defineProps({
  digit: {
    type: Number,
    required: true,
  },
  duration: {
    type: Number,
    default: 600,
  },
  fontSize: {
    type: Number,
  },
})

const width = computed(() => `${props.fontSize * 0.8}px`)
const height = computed(() => `${props.fontSize * 1.3}px`)
const textStyle = computed(() => ({
  height: height.value,
  fontSize: `${props.fontSize}px`,
  left: `${props.fontSize * 0.02}px`,
  top: `0`,
  bottom: `-${props.fontSize * 0.65}px`,
}))

const currentValue = ref(props.digit)
const nextValue = ref(null)
const previousValue = ref(null)
const isFlipping = ref(false)

const flippingTopRef = ref(null)
const flippingBottomRef = ref(null)

const flipTo = async (newValue) => {
  previousValue.value = currentValue.value
  currentValue.value = newValue
  nextValue.value = newValue
  isFlipping.value = true

  await nextTick()

  await Promise.all([
    gsapTimeline({ name: 'FlipCounter - Digit Top' }).fromTo(
      flippingTopRef.value.element,
      { rotateX: 0 },
      { rotateX: -90, duration: (props.duration * 0.5) / 1000, ease: 'cubic-bezier(0.37, 0.01, 0.94, 0.35)' }
    ),
    gsapTimeline({ name: 'FlipCounter - Digit Bottom' })
      .fromTo(flippingBottomRef.value.element, { rotateX: 90 }, { rotateX: 90, duration: props.duration / 2 / 1000, ease: 'cubic-bezier(0.15, 0.45, 0.28, 1)' })
      .to(flippingBottomRef.value.element, { rotateX: 0, duration: props.duration / 2 / 1000, ease: 'cubic-bezier(0.15, 0.45, 0.28, 1)' }),
  ])

  isFlipping.value = false
}

watch(
  () => props.digit,
  (newVal) => {
    if (newVal !== currentValue.value) {
      flipTo(newVal)
    }
  }
)
</script>

<template>
  <div
    class="relative flex items-center justify-center bg-joe-cr-blue-primary rounded-2xl border-2 border-joe-cr-blue-darker overflow-hidden shadow-sm shadow-joe-cr-blue-darker"
    :style="{ width, height }"
  >
    <template v-if="isFlipping">
      <FlipDigitPart :value="nextValue" position="top" :zIndex="2" :textStyle="textStyle" />
      <FlipDigitPart :value="previousValue" position="bottom" :zIndex="1" :textStyle="textStyle" />
      <FlipDigitPart ref="flippingTopRef" :value="previousValue" position="top" :zIndex="3" flipType="flipping-top" :textStyle="textStyle" />
      <FlipDigitPart ref="flippingBottomRef" :value="nextValue" position="bottom" :zIndex="4" flipType="flipping-bottom" :textStyle="textStyle" />
    </template>
    <template v-else>
      <FlipDigitPart :value="currentValue" position="top" :zIndex="2" :textStyle="textStyle" />
      <FlipDigitPart :value="currentValue" position="bottom" :zIndex="1" :textStyle="textStyle" />
    </template>
  </div>
</template>
